import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import moment from "moment-timezone";

// Soft UI Dashboard React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftSelect from "components/SoftComponents/SoftSelect";
import SoftButton from "components/SoftComponents/SoftButton";
import PropTypes from "prop-types";
import userAPI from "services/api/user";
import { loadingScreen } from "utils/wrapper";
import { useQuery } from "@tanstack/react-query";
import { Collapse, Switch } from "@mui/material";
import Swal from "sweetalert2";

const stat_types = {
    'battery_reading': "Battery",
    'rssi': "RSSI",
    // 'internal_temp': "Temperature",
    // 'internal_humidity': "Humidity"
}

// Validation Schema
const SubscriptionSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Required"),
    organisation_id: Yup.object(),
    timezone: Yup.string(),
    time_string: Yup.string().required("Required"),
    // fields: Yup.array().min(1, "At least one field is required.").required("Required"),
});

function EmailSettings({ user, deviceLayout, toggleActive, onSubmit }) {

    const [loading, setLoading] = useState(true);
    const [selectedSubId, setSelectedSubId] = useState(null);

    const subscriptions = useQuery({
        queryKey: ['getSubscriptionsForUser', user.user.email],
        queryFn: () => userAPI.getSubscriptionsForUser()
    });

    // 2. Auto-select logic: Watch the 'subscriptions' data
    useEffect(() => {
        if (subscriptions.data && subscriptions.data.length >= 1 && !selectedSubId) {
            setSelectedSubId(subscriptions.data[0]._id);
        }
    }, [subscriptions.data]);

    const activeSubscription = useMemo(() =>
        subscriptions.data?.find(s => s._id === selectedSubId),
        [subscriptions.data, selectedSubId]);

    const existingSubOrgIds = useMemo(() =>
        subscriptions.data?.map(s => s.organisation_id) || [],
        [subscriptions.data]);

    const availableOrgs = useMemo(() =>
        user.user.organisations
            .filter(org => !existingSubOrgIds.includes(org))
            .map(org => ({ value: org, label: org.toUpperCase().replace("-", " ") })),
        [user.user.organisations, existingSubOrgIds]);

    const initialValues = useMemo(() => {
        if (activeSubscription) {
            return {
                ...activeSubscription,
                time_string: `${Math.floor(activeSubscription.time_of_day / 60).toString().padStart(2, '0')}:${(activeSubscription.time_of_day % 60).toString().padStart(2, '0')}`,
                organisation_id: {
                    value: activeSubscription.organisation_id,
                    label: activeSubscription.organisation_id.toUpperCase().replace("-", " ")
                },
                active: !!activeSubscription.active
            };
        }
        // DEFAULT STATE for creating a new subscription
        return {
            // If only 1 org exists and it's available, auto-fill it
            organisation_id: availableOrgs.length === 1 ? availableOrgs[0] : { value: null, label: "New Subscription +" },
            timezone: moment.tz.guess(),
            email: user.user.email,
            frequency: "daily",
            time_string: "08:00",
            active: false, // UI is closed if no subscription exists
            days_of_week: [0, 1, 2, 3, 4],
            fields: ['battery_reading']
        };
    }, [activeSubscription, availableOrgs, user.user.email]);

    if (subscriptions.isLoading) {
        return loadingScreen(deviceLayout);
    }
    if (subscriptions.error) {
        return "An error has occurred: " + subscriptions.error.message;
    }
    if (!subscriptions.data) {
        return loadingScreen(deviceLayout);
    }

    if (loading) {
        loadingScreen(deviceLayout)
    };

    const handleSubmit = async (values, actions, run_alert = true) => {
        // 1. Convert "HH:mm" to minutesSinceMidnight for GCP
        const [hours, minutes] = values.time_string.split(":");
        const minutesSinceMidnight = parseInt(hours) * 60 + parseInt(minutes);

        // 2. Flatten the select objects for the API
        const payload = {
            ...values,
            organisation_id: values?.organisation_id?.value || initialValues.organisation_id,
            timezone: values?.timezone?.value || initialValues.timezone,
            time_of_day: minutesSinceMidnight,
        };

        console.log("GCP Payload:", payload); // FINISH POST TO DB

        try {
            if (run_alert) {
                Swal.fire({
                    icon: "warning",
                    title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Validating...</div>`,
                    html: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif;">Processing inspection data and images...</div>',
                    allowOutsideClick: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    },
                });
            }

            const result = await onSubmit(payload);
            console.log("Submission successful:", result);

            if (run_alert) {
                await Swal.fire({
                    icon: "success",
                    title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Saved!</div>',
                    showConfirmButton: false,
                    timer: 2000,
                });
            }

        }
        catch (err) {
            console.error("Submission failed:", err);
            const errorMessage = "Something went wrong while saving.";

            actions.setSubmitting(false);

            if (run_alert) {
                await Swal.fire({
                    icon: "error",
                    title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error!</div>',
                    html: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">${errorMessage}</div>`,
                    showConfirmButton: true,
                    timer: 15000,
                });
            }
        }
    };

    const hourOptions = Array.from({ length: 24 }, (_, i) => {
        const hour = i < 10 ? `0${i}` : i;
        return { value: `${hour}:00`, label: `${i % 12 || 12} ${i < 12 ? 'AM' : 'PM'}` };
    });

    return (
        <SoftBox p={3} sx={{ backgroundColor: "#f9f9f9", borderRadius: "1rem" }}>
            <Formik
                key={selectedSubId || "new"}
                enableReinitialize
                initialValues={initialValues}
                validationSchema={SubscriptionSchema}
                onSubmit={handleSubmit}
            >
                {({ values, errors, setFieldValue }) => (
                    <Form>
                        {/* --- HEADER SECTION --- */}
                        <SoftBox display="flex" flexDirection={{ xxs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xxs: "flex-start", md: "center" }} mb={values.active ? 3 : 0} gap={2}>
                            <SoftBox minWidth="150px">
                                <SoftTypography variant="h4" fontWeight="bold">Email Settings</SoftTypography>
                                <SoftTypography variant="button" fontWeight="regular" color="text">{values.active ? "Reports are scheduled" : "Reports are currently paused"}</SoftTypography>
                            </SoftBox>

                            {(user.user.organisations.length > 1) && (
                                <SoftBox sx={{ width: { xxs: "100%", md: "300px" } }}>
                                    <SoftSelect
                                        value={activeSubscription ? { value: activeSubscription._id, label: activeSubscription.organisation_id.toUpperCase().replace("-", " ") } : { value: null, label: "New Subscription +" }}
                                        options={[
                                            ...subscriptions.data.map(s => ({ value: s._id, label: s.organisation_id.toUpperCase().replace("-", " ") })),
                                            ...(availableOrgs.length > 0 ? [{ value: null, label: "New Subscription +" }] : [])
                                        ]}
                                        onChange={(opt) => setSelectedSubId(opt.value)}
                                    />
                                </SoftBox>
                            )}
                            <SoftBox display="flex" alignItems="center" sx={{ alignSelf: { xxs: "flex-end", md: "center" } }}>
                                <SoftTypography variant="caption" fontWeight="bold" mr={1}>
                                    {values.active ? "ON" : "OFF"}
                                </SoftTypography>
                                <Switch
                                    checked={values.active}
                                    onChange={(e) => {
                                        const nextValue = e.target.checked;
                                        if (user.user.organisations.length === 1 && subscriptions.data.length === 0) {
                                            if (nextValue === true) {
                                                handleSubmit({ ...values, active: true }, undefined, false);
                                            }
                                        }
                                        else if (selectedSubId) {
                                            toggleActive({ _id: selectedSubId, active: nextValue });
                                        }
                                    }}
                                />
                            </SoftBox>
                        </SoftBox>

                        <Collapse in={values.active} timeout={200} unmountOnExit>
                            <SoftBox component="div" role="form">

                                {/* Organisation Selection (Only shows when creating NEW) */}
                                {!selectedSubId && (
                                    <SoftBox mb={2}>
                                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" pl={0.5}>Assign to Organisation</SoftTypography>
                                        <SoftSelect
                                            placeholder="Choose an organisation..."
                                            value={values.organisation_id.value != null ? values.organisation_id : null}
                                            options={availableOrgs}
                                            onChange={(opt) => setFieldValue("organisation_id", opt)}
                                        />
                                        <SoftTypography variant="caption" color="text" sx={{ mt: 1, display: 'block' }}>
                                            Only organisations without an existing subscription are shown.
                                        </SoftTypography>
                                    </SoftBox>
                                )}

                                {/* Recipient Email (Disabled/ReadOnly) */}
                                <SoftBox mb={2}>
                                    <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" pl={0.5}>Recipient Email</SoftTypography>
                                    <SoftInput value={values.email} disabled />
                                </SoftBox>

                                {/* Frequency and Time Row */}
                                <SoftBox mb={2} display="flex" flexDirection={{ xxs: "column", sm: "row" }} gap={2}>
                                    <SoftBox flex={1}>
                                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" pl={0.5}>Frequency</SoftTypography>
                                        <SoftBox display="flex" gap={1} mt={1}>
                                            {["daily", "weekly"].map((f) => (
                                                <SoftButton
                                                    key={f}
                                                    variant={values.frequency === f ? "gradient" : "outlined"}
                                                    color="info" size="small" fullWidth
                                                    onClick={() => {
                                                        setFieldValue("frequency", f);
                                                        if (f === "weekly") setFieldValue("days_of_week", [0]);
                                                    }}
                                                >
                                                    {f}
                                                </SoftButton>
                                            ))}
                                        </SoftBox>
                                    </SoftBox>
                                    <SoftBox flex={1}>
                                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" pl={0.5}>Report Hour</SoftTypography>
                                        <SoftSelect
                                            value={hourOptions.find(opt => opt.value === values.time_string)}
                                            options={hourOptions}
                                            onChange={(opt) => setFieldValue("time_string", opt.value)}
                                        />
                                    </SoftBox>
                                </SoftBox>

                                {/* <SoftBox mb={2}>
                                    <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" pl={0.5}>Fields To Include</SoftTypography>
                                    <SoftBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                                        {Object.entries(stat_types).map(([key, label]) => {
                                            const isSelected = values.fields.includes(key);

                                            return (
                                                <SoftButton
                                                    key={key}
                                                    variant={isSelected ? "gradient" : "outlined"}
                                                    color={isSelected ? "info" : "secondary"}
                                                    size="small"
                                                    sx={{ flex: 1, minWidth: "100px" }} // Increased minWidth for better label fit
                                                    onClick={() => {
                                                        const next = isSelected
                                                            ? values.fields.filter((field) => field !== key)
                                                            : [...values.fields, key];

                                                        setFieldValue("fields", next);
                                                    }}
                                                >
                                                    {label}
                                                </SoftButton>
                                            );
                                        })}
                                    </SoftBox>
                                </SoftBox> */}

                                <SoftBox mb={3}>
                                    <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" pl={0.5}>Repeat On</SoftTypography>
                                    <SoftBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                                        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day, i) => (
                                            <SoftButton
                                                key={i}
                                                variant={values.days_of_week.includes(i) ? "gradient" : "outlined"}
                                                color={values.days_of_week.includes(i) ? "info" : "secondary"}
                                                size="small"
                                                sx={{ flex: 1, minWidth: "60px" }}
                                                onClick={() => {
                                                    const next = values.frequency === "weekly" ? [i] :
                                                        values.days_of_week.includes(i) ? values.days_of_week.filter(d => d !== i) : [...values.days_of_week, i];
                                                    setFieldValue("days_of_week", next);
                                                }}
                                            >
                                                {day}
                                            </SoftButton>
                                        ))}
                                    </SoftBox>
                                </SoftBox>

                                <SoftButton variant="gradient" color="dark" fullWidth type="submit">
                                    {selectedSubId ? "Update Settings" : "Create Subscription"}
                                </SoftButton>
                            </SoftBox>
                            {Object.keys(errors).length > 0 && (
                                <SoftBox mt={2}>
                                    <SoftTypography variant="caption" color="error">
                                        {'Error: ' + Object.values(errors).join(", ")}
                                    </SoftTypography>
                                </SoftBox>
                            )}
                        </Collapse>
                    </Form>
                )}
            </Formik>
        </SoftBox >
    );
}

EmailSettings.propTypes = {
    user: PropTypes.object,
    deviceLayout: PropTypes.string,
    toggleActive: PropTypes.func,
    onSubmit: PropTypes.func,
}

export default EmailSettings;